// Generates a comprehensive PDF summary of all thermal resistance calculator results
// Includes all assembly calculations with location and compliance information
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import logo from '../../../PageBuilder/interface/images/logo.png';
import generateResultsPdf from '../assemblies/WallsAboveGrade/pdfResults';
import { getClimateZone } from '../helpers';

const isNum = v => typeof v === 'number' && Number.isFinite(v);

const STRIPE_FILL = [248, 248, 248];
const TABLE_FILL = [255, 255, 255];

function titleText(doc, text, x, y) {
  if (!text) return;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.setFontSize(18);
  doc.text(String(text), x, y);
}

function paragraph(doc, text, x, y, maxWidth) {
  if (!text) return;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0);
  doc.setFontSize(10);
  doc.text(String(text), x, y, { maxWidth });
}

// Build a generic rowsConfig from the results data for use with generateResultsPdf
function buildRowsConfigFromResults(results) {
  if (!results) return [];

  const rows = [];

  if (results.effectiveAssemblyRSI != null) {
    rows.push({ label: 'Effective Assembly RSI', rsi: 'effectiveAssemblyRSI' });
  }

  if (results.centerOfCavityRSI != null) {
    rows.push({ label: 'Center of Cavity RSI', rsi: 'centerOfCavityRSI' });
  }

  if (results.installedInsulationRSI != null) {
    rows.push({ label: 'Installed Insulation RSI', rsi: 'installedInsulationRSI' });
  }

  return rows;
}

export default function generateSummaryPdf({ province, region, hdd, resultsByCalc, assemblies, t }) {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const LEFT = 40;
  const TOP = 40;

  // Create cover page with location and climate information
  let logoH = 40;
  const LOGO_W = 40;

  try {
    const props = doc.getImageProperties(logo);
    const ratio = props.height / props.width || 1;
    logoH = LOGO_W * ratio;
    doc.addImage(logo, 'PNG', LEFT, TOP, LOGO_W, logoH);
  } catch {
    doc.addImage(logo, 'PNG', LEFT, TOP, LOGO_W, logoH);
  }

  const titleY = TOP + logoH + 25;
  titleText(doc, t('thermal_calculator.pdf.all_results_title'), LEFT, titleY);

  const introY = titleY + 24;
  const rightMargin = 40;
  const introMaxW = pageW - LEFT - rightMargin;
  paragraph(
    doc,
    // eslint-disable-next-line max-len
    t('thermal_calculator.pdf_disclaimer'),
    LEFT,
    introY,
    introMaxW
  );

  autoTable(doc, {
    startY: introY + 60,
    margin: { left: LEFT },
    tableWidth: 280,
    head: [],
    body: [
      [t('thermal_calculator.results.province'), province || '—'],
      [t('thermal_calculator.results.region'), region || '—'],
      [t('thermal_calculator.results.heating_degree_days'), isNum(hdd) ? hdd : '—'],
    ],
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 8, halign: 'left', valign: 'middle' },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 160 }, 1: { cellWidth: 150 } },
    theme: 'grid',
    didParseCell: data => {
      if (data.section === 'body' && data.row.index % 2 === 0) {
        // eslint-disable-next-line no-param-reassign
        data.cell.styles.fillColor = STRIPE_FILL;
      } else if (data.section === 'body') {
        // eslint-disable-next-line no-param-reassign
        data.cell.styles.fillColor = TABLE_FILL;
      }
    },
  });

  const climateZone = getClimateZone(hdd);
  const y = (doc.lastAutoTable?.finalY || introY + 14) + 20;

  autoTable(doc, {
    startY: y,
    margin: { left: LEFT },
    tableWidth: 280,
    head: [],
    body: [[t('thermal_calculator.header.climate_zone'), climateZone || '—']],
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 8, halign: 'left', valign: 'middle' },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 160 }, 1: { cellWidth: 150 } },
    theme: 'grid',
    didParseCell: data => {
      if (data.section === 'body') {
        // eslint-disable-next-line no-param-reassign
        data.cell.styles.fillColor = TABLE_FILL;
      }
    },
  });

  // Generate detailed pages for each assembly using the shared pdfResults function
  const assemblyKeys = Object.keys(assemblies);

  assemblyKeys.forEach(calcId => {
    const assemblyData = resultsByCalc?.[calcId];
    const results = assemblyData?.results;
    const meta = assemblyData?.meta;

    if (!results) return;

    // Build a generic rowsConfig from the available results
    const rowsConfig = buildRowsConfigFromResults(results);

    // Call the shared pdfResults function to add pages for this assembly
    generateResultsPdf({
      calcId,
      province,
      region,
      hdd,
      assemblyHeading: meta?.title || calcId,
      tableTitle: t('thermal_calculator.entire_assembly'),
      tableSubtitle: t('thermal_calculator.effective_thermal_resistance'),
      rowsConfig,
      results,
      footnotes: [],
      existingDoc: doc,
      skipSave: true,
      t,
    });
  });

  // Add disclaimer footer on a final page if needed
  const currentPage = doc.internal.getNumberOfPages();
  doc.setPage(currentPage);
  const currentY = doc.lastAutoTable?.finalY || TOP;

  if (currentY < pageH - 100) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60);
    doc.setFontSize(9);
    doc.text(t('thermal_calculator.pdf.consult_building_code'), LEFT, currentY + 40);
  }

  // Add page numbers to all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Page ${i} of ${pageCount}`, pageW - 80, pageH - 24);
  }

  const fname = `Thermal_Results_Summary_${province || 'NA'}_${region || 'NA'}.pdf`;
  doc.save(fname);
}
